import { useState, useEffect, useCallback } from "react";
import {
  ShieldCheck,
  Building2,
  Plus,
  Copy,
  ExternalLink,
  Users,
  TrendingUp,
  CheckCircle2,
  Clock,
  Search,
  RefreshCw,
  Sparkles,
  AlertCircle,
  LogOut,
  Lock,
  Mail,
  Trash2,
  ArrowRight,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { ACTIVE_CRM_STORAGE_KEY } from "../context/CRMContext";
import { getSuperAdminEmail, isSuperAdminEmail } from "./superadminConfig";
import type { CRMRecord } from "../types";
import { SuperAdminWebhookCard } from "./SuperAdminWebhookCard";

export const SuperAdminPortal = () => {
  // Autenticação do Super Admin
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  // Gestão de CRMs
  const [crms, setCrms] = useState<CRMRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [createdCrmInfo, setCreatedCrmInfo] = useState<{
    id: string;
    name: string;
    slug: string;
    token: string;
  } | null>(null);

  // Exclusão de CRM
  const [crmToDelete, setCrmToDelete] = useState<CRMRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Formulário de criação
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [enableVoip, setEnableVoip] = useState(false);
  const [enableTranscription, setEnableTranscription] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Verifica se o usuário atual é Super Admin
  const checkAuth = useCallback(async () => {
    try {
      const client = getSupabaseClient();
      const { data: sessionData } = await client.auth.getSession();
      const user = sessionData?.session?.user;

      if (!user) {
        setIsAuthenticated(false);
        return;
      }

      const email = user.email?.toLowerCase() || "";
      setCurrentUserEmail(email);

      // Verifica se é o email do super admin configurado no .env
      const superAdminEmail = getSuperAdminEmail();
      if (isSuperAdminEmail(email)) {
        setIsAuthenticated(true);
        try {
          if (superAdminEmail) {
            await client.from("sales").delete().eq("email", superAdminEmail);
          }
          await client.from("crm_members").delete().eq("user_id", user.id);
        } catch {}
        return;
      }

      const { data: salesUser } = await client
        .from("sales")
        .select("is_superadmin")
        .eq("user_id", user.id)
        .maybeSingle();

      if (salesUser?.is_superadmin) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error("Erro ao verificar autenticação de Super Admin:", err);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Carrega CRMs
  const fetchCrms = useCallback(async () => {
    setIsLoading(true);
    try {
      const client = getSupabaseClient();
      const { data, error } = await client
        .from("crms")
        .select(
          `
          *,
          crm_members(count),
          deals(count)
        `,
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        const enriched = data.map((item: any) => ({
          ...item,
          member_count: item.crm_members?.[0]?.count || 0,
          deals_count: item.deals?.[0]?.count || 0,
        }));
        setCrms(enriched);
      }
    } catch (err) {
      console.error("Erro ao carregar CRMs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCrms();
    }
  }, [isAuthenticated, fetchCrms]);

  // Handler de Login Dedicado do Super Admin
  const handleSuperAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");

    try {
      const client = getSupabaseClient();
      const targetEmail = loginEmail.trim().toLowerCase();

      // Validação da senha feita EXCLUSIVAMENTE no servidor (a senha não existe no bundle JS)
      const syncRes = await fetch("/api/superadmin-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: targetEmail,
          password: loginPassword,
        }),
      });

      if (!syncRes.ok) {
        const errData = await syncRes.json().catch(() => ({}));
        throw new Error(
          errData?.error?.message ||
            errData?.error ||
            "E-mail ou senha do Superadministrador incorretos.",
        );
      }

      // Autentica a sessão do cliente via Supabase
      const { data: signInData, error: signInErr } =
        await client.auth.signInWithPassword({
          email: targetEmail,
          password: loginPassword,
        });

      if (signInErr || !signInData?.user) {
        throw new Error(
          signInErr?.message ||
            "E-mail ou senha do Superadministrador incorretos.",
        );
      }

      const user = signInData.user;

      // 2. Valida se o usuário autenticado realmente possui perfil de Super Administrador
      const { data: salesUser } = await client
        .from("sales")
        .select("is_superadmin")
        .eq("user_id", user.id)
        .maybeSingle();

      const isAllowed = Boolean(
        targetEmail === "tecnologia@grupodotus.com.br" ||
          isSuperAdminEmail(targetEmail) ||
          salesUser?.is_superadmin,
      );

      if (!isAllowed) {
        await client.auth.signOut();
        throw new Error(
          "Acesso negado: Este usuário não possui privilégios de Superadministrador.",
        );
      }

      // 3. Remove o Super Admin de qualquer tabela de vendas ou membros de CRM de clientes
      try {
        await client
          .from("sales")
          .delete()
          .or(`email.eq.${targetEmail},user_id.eq.${user.id}`);

        await client.from("crm_members").delete().eq("user_id", user.id);
      } catch (dbErr) {
        console.warn(
          "Aviso ao limpar Super Admin das tabelas de equipe:",
          dbErr,
        );
      }

      setIsAuthenticated(true);
      setCurrentUserEmail(targetEmail);
      setLoginPassword(""); // Limpa a senha da memória
    } catch (err: any) {
      console.error("Erro no login do Superadmin:", err);
      setLoginError(err.message || "Falha na autenticação do Super Admin.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const client = getSupabaseClient();
      await client.auth.signOut();
      setIsAuthenticated(false);
      setCurrentUserEmail("");
    } catch (err) {
      console.error("Erro ao sair:", err);
    }
  };

  // Gerador automático de slug a partir do nome
  const handleNameChange = (val: string) => {
    setNewName(val);
    const slug = val
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setNewSlug(slug);
  };

  const handleCreateCrm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!newName.trim() || !newSlug.trim()) {
      setFormError("Preencha o nome e o identificador (slug) do CRM.");
      return;
    }

    setIsSubmitting(true);
    try {
      const client = getSupabaseClient();
      let createdId = "";

      // RPC segura para criação com templates de funil
      const { data, error } = await client.rpc("create_crm_with_defaults", {
        p_name: newName.trim(),
        p_slug: newSlug.trim(),
      });

      if (error) {
        // Fallback direto
        const token =
          "act_" +
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        const { data: insertData, error: insertError } = await client
          .from("crms")
          .insert({
            name: newName.trim(),
            slug: newSlug.trim(),
            activation_token: token,
            status: "pending_activation",
            settings: {
              voip_enabled: enableVoip,
              transcription_enabled: enableTranscription,
            },
          })
          .select()
          .single();

        if (insertError) throw insertError;

        createdId = insertData.id;
        setCreatedCrmInfo({
          id: insertData.id,
          name: insertData.name,
          slug: insertData.slug,
          token: insertData.activation_token,
        });
      } else {
        createdId = data.id;
        setCreatedCrmInfo({
          id: data.id,
          name: data.name,
          slug: data.slug,
          token: data.activation_token,
        });
      }

      // Salva as configurações de módulos no CRM recém-criado
      if (createdId) {
        await client
          .from("crms")
          .update({
            settings: {
              voip_enabled: enableVoip,
              transcription_enabled: enableTranscription,
            },
          })
          .eq("id", createdId);
      }

      setIsCreateOpen(false);
      setIsSuccessOpen(true);
      setNewName("");
      setNewSlug("");
      setEnableVoip(false);
      setEnableTranscription(false);
      await fetchCrms();
      toast.success("Novo CRM criado com sucesso!");
    } catch (err: any) {
      console.error("Erro ao criar CRM:", err);
      setFormError(err.message || "Erro ao criar novo CRM.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleModule = async (
    crmId: string,
    key: "voip_enabled" | "transcription_enabled",
    newValue: boolean,
  ) => {
    try {
      const client = getSupabaseClient();
      const targetCrm = crms.find((c) => c.id === crmId);
      const currentSettings = targetCrm?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        [key]: newValue,
      };

      const { error } = await client
        .from("crms")
        .update({ settings: updatedSettings })
        .eq("id", crmId);

      if (error) throw error;

      setCrms((prev) =>
        prev.map((c) =>
          c.id === crmId ? { ...c, settings: updatedSettings } : c,
        ),
      );

      const moduleName = key === "voip_enabled" ? "VoIP" : "Transcrição IA";
      toast.success(
        `${moduleName} ${newValue ? "ativado" : "desativado"} para ${targetCrm?.name || "o CRM"}.`,
      );
    } catch (err: any) {
      console.error("Erro ao alterar módulo do CRM:", err);
      toast.error(
        "Falha ao atualizar módulo: " + (err.message || "Erro desconhecido"),
      );
    }
  };

  const handleDeleteCrm = async () => {
    if (!crmToDelete) return;
    setIsDeleting(true);
    try {
      const client = getSupabaseClient();

      // 1. Obtém os IDs dos membros exclusivos deste CRM antes da exclusão
      const { data: members } = await client
        .from("crm_members")
        .select("sales_id")
        .eq("crm_id", crmToDelete.id);

      const targetSalesIds = (members || [])
        .map((m: any) => m.sales_id)
        .filter(Boolean);

      // 2. Exclui o CRM (ativa ON DELETE CASCADE nas tabelas filhas)
      const { error } = await client
        .from("crms")
        .delete()
        .eq("id", crmToDelete.id);

      if (error) throw error;

      // 3. Remove os usuários exclusivos deste CRM da tabela sales
      if (targetSalesIds.length > 0) {
        for (const sid of targetSalesIds) {
          const { data: otherLinks } = await client
            .from("crm_members")
            .select("id")
            .eq("sales_id", sid);

          if (!otherLinks || otherLinks.length === 0) {
            await client.from("sales").delete().eq("id", sid);
          }
        }
      }

      // 4. Limpa do localStorage se o CRM ativo local for o deletado
      if (localStorage.getItem(ACTIVE_CRM_STORAGE_KEY) === crmToDelete.id) {
        localStorage.removeItem(ACTIVE_CRM_STORAGE_KEY);
      }

      setCrms((prev) => prev.filter((c) => c.id !== crmToDelete.id));
      setCrmToDelete(null);
    } catch (err: any) {
      console.error("Erro ao excluir CRM:", err);
      alert("Erro ao excluir CRM: " + (err.message || "Tente novamente"));
    } finally {
      setIsDeleting(false);
    }
  };

  const getActivationLink = (token: string | null | undefined) => {
    if (!token) return "";
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://app.dotuscrm.com";
    return `${origin}/#/activate-crm?token=${encodeURIComponent(token)}`;
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleAccessCrm = (crmId: string) => {
    localStorage.setItem(ACTIVE_CRM_STORAGE_KEY, crmId);
    window.location.replace("/#/");
    window.location.reload();
  };

  // Se a verificação inicial ainda está rodando
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground text-xs">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
          <span>Verificando credenciais de Superadministrador...</span>
        </div>
      </div>
    );
  }

  // TELA DE LOGIN DEDICADA DO SUPER ADMIN
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4 sm:p-6 animate-in fade-in-50 duration-300">
        <Card className="max-w-md w-full border-border/80 shadow-2xl overflow-hidden">
          <div className="bg-primary/10 border-b border-primary/20 p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 shadow-md">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">
              Portal Superadministrador
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Área restrita para criação e gestão central de todos os CRMs
            </p>
          </div>

          <CardContent className="p-6 space-y-4">
            {loginError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{loginError}</span>
              </div>
            )}

            <form onSubmit={handleSuperAdminLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="superEmail" className="text-xs font-medium">
                  E-mail do Superadministrador
                </Label>
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                  <Input
                    id="superEmail"
                    type="email"
                    placeholder="seu-email@dominio.com"
                    autoComplete="off"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="pl-8 h-9 text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="superPassword" className="text-xs font-medium">
                  Senha de Acesso Master
                </Label>
                <div className="relative">
                  <Lock className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                  <Input
                    id="superPassword"
                    type="password"
                    placeholder="••••••••••••"
                    autoComplete="off"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="pl-8 h-9 text-xs"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full text-xs font-semibold gap-2 h-10 shadow-sm mt-2"
              >
                {loginLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Autenticando Super Admin...
                  </>
                ) : (
                  <>
                    Acessar Painel Central
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="pt-2 text-center">
              <a
                href="/#/login"
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Voltar para o login de usuário comum
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // TELA DO PAINEL PRINCIPAL DO SUPER ADMIN
  const filteredCrms = crms.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.slug.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalActive = crms.filter((c) => c.status === "active").length;
  const totalPending = crms.filter(
    (c) => c.status === "pending_activation",
  ).length;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Top Navbar Dedicada do Super Admin */}
      <header className="h-16 border-b border-border/80 bg-card/60 backdrop-blur-md px-6 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-base tracking-tight">
                Dotus Platform
              </span>
              <Badge
                variant="outline"
                className="text-[10px] bg-primary/10 text-primary border-primary/20"
              >
                Super Admin
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Console de Gerenciamento Multilocatário
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <span className="text-[10px] text-muted-foreground block">
              Conectado como
            </span>
            <span className="text-xs font-semibold text-foreground">
              {currentUserEmail}
            </span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-xs text-muted-foreground hover:text-destructive gap-1.5 h-8"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sair do Console</span>
          </Button>
        </div>
      </header>

      {/* Conteúdo do Painel */}
      <main className="flex-1 p-6 max-w-7xl w-full mx-auto space-y-6">
        {/* Banner Superior */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Gestão de Ambientes de CRM
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Crie novos CRMs, copie links de ativação de proprietários e acerte
              permissões de forma 100% isolada.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCrms}
              disabled={isLoading}
              className="gap-2 text-xs h-9"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`}
              />
              Atualizar
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              className="gap-2 shadow-sm text-xs h-9"
            >
              <Plus className="w-3.5 h-3.5" />
              Criar Novo CRM
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-border/60 bg-card/60">
            <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total de CRMs
              </CardTitle>
              <Building2 className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{crms.length}</div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Ambientes corporativos criados
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/60">
            <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                CRMs Ativos
              </CardTitle>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {totalActive}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Com dono ativado e operando
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/60">
            <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Aguardando Ativação
              </CardTitle>
              <Clock className="w-4 h-4 text-amber-500" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {totalPending}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Link de dono gerado mas não ativado
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Webhook Geral & Chaves de Acesso do Superadmin */}
        <SuperAdminWebhookCard />

        {/* Tabela de Ambientes */}
        <Card className="border-border/60">
          <CardHeader className="p-4 border-b border-border/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-semibold">
                Lista de Ambientes de CRM
              </CardTitle>
              <CardDescription className="text-xs">
                Visualize os detalhes, status e acesse qualquer ambiente.
              </CardDescription>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou slug..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs bg-background"
              />
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse">
                <thead>
                  <tr className="border-b border-border/40 bg-muted/30 text-muted-foreground">
                    <th className="py-2.5 px-4 font-semibold">Nome do CRM</th>
                    <th className="py-2.5 px-4 font-semibold">Slug (ID)</th>
                    <th className="py-2.5 px-4 font-semibold">Status</th>
                    <th className="py-2.5 px-4 font-semibold">
                      Módulos (VoIP / IA)
                    </th>
                    <th className="py-2.5 px-4 font-semibold">Membros</th>
                    <th className="py-2.5 px-4 font-semibold">Negócios</th>
                    <th className="py-2.5 px-4 font-semibold">Criado em</th>
                    <th className="py-2.5 px-4 font-semibold text-right">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {filteredCrms.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-8 text-center text-muted-foreground text-xs"
                      >
                        Nenhum CRM cadastrado ou encontrado.
                      </td>
                    </tr>
                  ) : (
                    filteredCrms.map((crm) => {
                      const activationUrl = getActivationLink(
                        crm.activation_token,
                      );
                      return (
                        <tr
                          key={crm.id}
                          className="hover:bg-muted/20 transition-colors"
                        >
                          <td className="py-3 px-4 font-medium text-foreground">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase">
                                {crm.name.slice(0, 2)}
                              </div>
                              <div>
                                <div className="font-semibold">{crm.name}</div>
                                <div className="text-[10px] text-muted-foreground font-mono">
                                  {crm.id.slice(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-4 font-mono text-[11px] text-muted-foreground">
                            {crm.slug}
                          </td>

                          <td className="py-3 px-4">
                            {crm.status === "active" ? (
                              <Badge
                                variant="outline"
                                className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Ativo
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px]"
                              >
                                <Clock className="w-3 h-3 mr-1" />
                                Aguardando Dono
                              </Badge>
                            )}
                          </td>

                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {/* Toggle VoIP */}
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleModule(
                                    crm.id,
                                    "voip_enabled",
                                    !crm.settings?.voip_enabled,
                                  )
                                }
                                title={
                                  crm.settings?.voip_enabled
                                    ? "VoIP Ativo (clique para desativar)"
                                    : "VoIP Inativo (clique para ativar)"
                                }
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border cursor-pointer ${
                                  crm.settings?.voip_enabled
                                    ? "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 hover:bg-blue-500/25"
                                    : "bg-muted/70 text-muted-foreground border-border/50 opacity-60 hover:opacity-100 hover:border-border"
                                }`}
                              >
                                <Phone className="w-3 h-3" />
                                <span>VoIP</span>
                                <span className="text-[9px] font-bold">
                                  {crm.settings?.voip_enabled ? "ON" : "OFF"}
                                </span>
                              </button>

                              {/* Toggle Transcrição IA */}
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleModule(
                                    crm.id,
                                    "transcription_enabled",
                                    !crm.settings?.transcription_enabled,
                                  )
                                }
                                title={
                                  crm.settings?.transcription_enabled
                                    ? "Transcrição IA Ativa (clique para desativar)"
                                    : "Transcrição IA Inativa (clique para ativar)"
                                }
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border cursor-pointer ${
                                  crm.settings?.transcription_enabled
                                    ? "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30 hover:bg-purple-500/25"
                                    : "bg-muted/70 text-muted-foreground border-border/50 opacity-60 hover:opacity-100 hover:border-border"
                                }`}
                              >
                                <Sparkles className="w-3 h-3" />
                                <span>IA</span>
                                <span className="text-[9px] font-bold">
                                  {crm.settings?.transcription_enabled
                                    ? "ON"
                                    : "OFF"}
                                </span>
                              </button>
                            </div>
                          </td>

                          <td className="py-3 px-4 text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5 opacity-70" />
                              <span>{crm.member_count || 1}</span>
                            </div>
                          </td>

                          <td className="py-3 px-4 text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <TrendingUp className="w-3.5 h-3.5 opacity-70" />
                              <span>{crm.deals_count || 0}</span>
                            </div>
                          </td>

                          <td className="py-3 px-4 text-muted-foreground">
                            {new Date(crm.created_at).toLocaleDateString(
                              "pt-BR",
                            )}
                          </td>

                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {crm.activation_token && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    copyToClipboard(activationUrl, crm.id)
                                  }
                                  title="Copiar Link de Ativação do Dono"
                                  className="h-7 text-xs px-2 gap-1 border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                                >
                                  {copiedId === crm.id ? (
                                    <>
                                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                      <span>Copiado!</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-3 h-3" />
                                      <span>Link Dono</span>
                                    </>
                                  )}
                                </Button>
                              )}

                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleAccessCrm(crm.id)}
                                className="h-7 text-xs px-2.5 gap-1 shadow-xs"
                              >
                                <ExternalLink className="w-3 h-3" />
                                <span>Acessar</span>
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCrmToDelete(crm)}
                                title="Excluir CRM"
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Modal de Criação de Novo CRM */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreateCrm}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Criar Novo CRM
              </DialogTitle>
              <DialogDescription className="text-xs">
                O CRM nascerá sem membros e com um link de ativação exclusivo
                para o Dono.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {formError && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="crmName" className="text-xs font-medium">
                  Nome da Empresa / CRM *
                </Label>
                <Input
                  id="crmName"
                  placeholder="Ex: Acme Tecnologia"
                  value={newName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  className="h-9 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="crmSlug" className="text-xs font-medium">
                  Identificador Único (Slug) *
                </Label>
                <Input
                  id="crmSlug"
                  placeholder="Ex: acme-tecnologia"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  required
                  className="h-9 text-xs font-mono"
                />
                <p className="text-[10px] text-muted-foreground">
                  Identificador para rotas e isolamento multilocatário.
                </p>
              </div>

              {/* Módulos opcionais do CRM: VoIP e Transcrição IA */}
              <div className="space-y-2.5 pt-1">
                <Label className="text-xs font-semibold text-foreground">
                  Módulos Adicionais (Ativar sob demanda)
                </Label>

                {/* Switch VoIP */}
                <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/70 bg-muted/20 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start gap-2.5 pr-2">
                    <span className="p-1 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 mt-0.5">
                      <Phone className="w-3.5 h-3.5" />
                    </span>
                    <div>
                      <Label
                        htmlFor="enableVoipPortal"
                        className="text-xs font-medium cursor-pointer"
                      >
                        Habilitar Telefonia VoIP (API4COM)
                      </Label>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Permite que os usuários deste CRM façam ligações pelo
                        discador integrado.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="enableVoipPortal"
                    checked={enableVoip}
                    onCheckedChange={setEnableVoip}
                  />
                </div>

                {/* Switch Transcrição IA */}
                <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/70 bg-muted/20 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start gap-2.5 pr-2">
                    <span className="p-1 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5" />
                    </span>
                    <div>
                      <Label
                        htmlFor="enableTranscriptionPortal"
                        className="text-xs font-medium cursor-pointer"
                      >
                        Habilitar Transcrição & Auditoria IA
                      </Label>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Transcreve áudios com Whisper e gera auditorias
                        comerciais completas com Gemini.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="enableTranscriptionPortal"
                    checked={enableTranscription}
                    onCheckedChange={setEnableTranscription}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsCreateOpen(false)}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                size="sm"
                className="text-xs gap-1.5"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    Criar CRM
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Sucesso após criação com link de ativação */}
      <Dialog open={isSuccessOpen} onOpenChange={setIsSuccessOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-2">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <DialogTitle className="text-base font-bold">
              CRM Criado com Sucesso!
            </DialogTitle>
            <DialogDescription className="text-xs">
              Envie o link abaixo para o cliente ou responsável pela empresa.
              Ele será o <strong>Dono oficial (Owner)</strong> com acesso
              completo.
            </DialogDescription>
          </DialogHeader>

          {createdCrmInfo && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-muted/40 rounded-lg border space-y-1 text-xs">
                <div className="font-semibold text-foreground">
                  {createdCrmInfo.name}
                </div>
                <div className="text-muted-foreground font-mono text-[11px]">
                  Slug: {createdCrmInfo.slug}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Link de Ativação do Dono
                </Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={getActivationLink(createdCrmInfo.token)}
                    className="h-9 text-xs font-mono bg-muted/30"
                  />
                  <Button
                    size="sm"
                    className="shrink-0 text-xs gap-1.5"
                    onClick={() =>
                      copyToClipboard(
                        getActivationLink(createdCrmInfo.token),
                        "success_modal",
                      )
                    }
                  >
                    {copiedId === "success_modal" ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copiar Link
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsSuccessOpen(false)}
              className="text-xs"
            >
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão de CRM */}
      <Dialog
        open={Boolean(crmToDelete)}
        onOpenChange={(open) => !open && setCrmToDelete(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Excluir Ambiente de CRM
            </DialogTitle>
            <DialogDescription className="text-xs">
              Tem certeza de que deseja excluir permanentemente o CRM{" "}
              <strong>{crmToDelete?.name}</strong>? Todos os dados, negócios e
              contatos vinculados serão removidos.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              disabled={isDeleting}
              onClick={() => setCrmToDelete(null)}
              className="text-xs"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={isDeleting}
              onClick={handleDeleteCrm}
              className="text-xs gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {isDeleting ? "Excluindo..." : "Confirmar Exclusão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
